import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { DatePicker } from "design-system/molecules/DatePicker"
import { AlertTriangle, RefreshCcw } from "lucide-react"
import { useRegenerateMetricsForDayMutation } from "modules/backoffice/hooks/bi/useRegenerateMetricsForDayMutation"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

export function RegenerateMetricsDialog() {
  const [date, setDate] = useState<Date | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const { mutateAsync: regenerateMetricsForDay, isPending } = useRegenerateMetricsForDayMutation()

  function onDateChange(date?: Date) {
    setDate(undefined)

    if (!date) return

    // NOTE: Making sure the date is at central time (UTC), the one used by the backend
    const [year, month, day] = [date.getFullYear(), date.getMonth(), date.getDate()]
    const utcMidnight = new Date(Date.UTC(year, month, day))
    setDate(utcMidnight)
  }

  async function handleRegenerateMetricsForDay() {
    if (!date) return

    const response = await regenerateMetricsForDay({ date: date.toISOString() })
    if (response.success) {
      toast.success(t("backoffice.bi.regenerateMetrics.success"))
      setOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button variant="default" className="h-10 w-10">
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("backoffice.bi.regenerateMetrics.title")}</DialogTitle>
          <DialogDescription>{t("backoffice.bi.regenerateMetrics.description")}</DialogDescription>
        </DialogHeader>
        <Alert variant="warning">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>{t("backoffice.bi.regenerateMetrics.alert.title")}</AlertTitle>
          <AlertDescription>
            {t("backoffice.bi.regenerateMetrics.alert.description")}
          </AlertDescription>
        </Alert>
        <div className="flex flex-col gap-2">
          <DatePicker date={date} onDateChange={onDateChange} />
        </div>
        <DialogFooter>
          <Button variant="outline">{t("shared.cancel")}</Button>
          <Button
            variant="default"
            disabled={isPending || !date}
            onClick={handleRegenerateMetricsForDay}
          >
            {t("backoffice.bi.regenerateMetrics.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
